import React, { useEffect, useState } from "react";
import { useMediaQuery, Tooltip } from "@mui/material";
import BlueButton from "../../../components/BlueButton/BlueButton";
import { DataGrid } from "@mui/x-data-grid";
import SwipeableModals from "../SwipeableModals/SwipeableModals";
import MessageModals from "../../../components/MessageModals/MessageModals";
import DeleteUser from "./../../../assets/delete.svg";
import EditUser from "./../../../assets/edit-user.svg";
import AddGif from "./../../../assets/add-gif.gif";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import { usePermissions } from "../../../hooks/usePermissions";

const Terms = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  const [isOpen, setIsOpen] = useState(false);
  const [currentModalView, setCurrentModalView] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reFetch, setReFetch] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [termToDelete, setTermToDelete] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Check permissions
  const canView = hasPermission(99); // رؤية كل الشروط
  const canAdd = hasPermission(98); // اضافة شرط جديد
  const canEdit = hasPermission(100); // تعديل شرط
  const canDelete = hasPermission(101); // حذف شرط

  useEffect(() => {
    const getData = async () => {
      // Check view permission
      if (!canView) {
        toast.error("ليس لديك صلاحية لعرض الشروط والأحكام");
        return;
      }

      setLoading(true);
      try {
        const res = await api.get(
          `/system/terms-conditions?page=${page}&limit=${pageSize}`
        );
        setData(res.data.data);
        setTotalCount(res.data.totalCount);
      } catch (error) {
        console.error("Error fetching terms:", error);
        toast.error("حدث خطأ أثناء تحميل البيانات");
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [page, pageSize, reFetch, canView]);

  const handleOpen = (view, term = null) => {
    // Check permissions based on the view
    if (view === "createTerm" && !canAdd) {
      toast.error("ليس لديك صلاحية لإضافة شرط جديد");
      return;
    }
    if (view === "editTerm" && !canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الشرط");
      return;
    }

    setSelectedTerm(term);
    setCurrentModalView(view);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedTerm(null);
  };

  const handleDelete = (term) => {
    // Check delete permission
    if (!canDelete) {
      toast.error("ليس لديك صلاحية لحذف الشرط");
      return;
    }

    setTermToDelete(term);
    setOpenDelete(true);
  };

  const handleAddClick = () => {
    // Check add permission
    if (!canAdd) {
      toast.error("ليس لديك صلاحية لإضافة شرط جديد");
      return;
    }
    handleOpen("createTerm");
  };

  const handleEditClick = (term) => {
    // Check edit permission
    if (!canEdit) {
      toast.error("ليس لديك صلاحية لتعديل الشرط");
      return;
    }
    handleOpen("editTerm", term);
  };

  const columns = [
    {
      field: "index",
      headerName: "#",
      minWidth: 20,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "titleAr",
      headerName: "عنوان النص بالعربي",
      minWidth: 160,
      headerAlign: "center",
      align: "center",
      flex: 1,
    },
    {
      field: "titleEn",
      headerName: "عنوان النص بالانجليزية",
      minWidth: 160,
      headerAlign: "center",
      align: "center",
      flex: 1,
    },
    {
      field: "textAr",
      headerName: "النص بالعربية",
      minWidth: 220,
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => {
        return (
          <div className="text-cell">
            {params.row.textAr?.length > 50
              ? `${params.row.textAr.substring(0, 50)}...`
              : params.row.textAr}
          </div>
        );
      },
    },
    {
      field: "textEn",
      headerName: "النص بالانجليزية",
      minWidth: 220,
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => {
        return (
          <div className="text-cell">
            {params.row.textEn?.length > 50
              ? `${params.row.textEn.substring(0, 50)}...`
              : params.row.textEn}
          </div>
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 240,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            {/* Edit Button */}
            <Tooltip
              title={canEdit ? "تعديل الشرط" : "ليس لديك صلاحية لتعديل الشرط"}
            >
              <div
                className="cell-btn"
                onClick={() => handleEditClick(params.row)}
                style={{
                  cursor: canEdit ? "pointer" : "not-allowed",
                  opacity: canEdit ? 1 : 0.5,
                }}
              >
                <img src={EditUser} alt="تعديل" />
              </div>
            </Tooltip>

            {/* Delete Button */}
            <Tooltip
              title={canDelete ? "حذف الشرط" : "ليس لديك صلاحية لحذف الشرط"}
            >
              <div
                className="cell-btn"
                onClick={() => handleDelete(params.row)}
                style={{
                  cursor: canDelete ? "pointer" : "not-allowed",
                  opacity: canDelete ? 1 : 0.5,
                }}
              >
                <img src={DeleteUser} alt="حذف" />
              </div>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  // If no view permission, show restricted access
  if (!canView) {
    return (
      <div className="departments-container">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <p
            style={{
              fontSize: "18px",
              color: "#666",
              textAlign: "center",
            }}
          >
            ليس لديك صلاحية لعرض الشروط والأحكام
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="departments-container">
      <div className="actions-container" style={{ marginBottom: "20px" }}>
        {canAdd ? (
          <BlueButton
            text="اضافة شرط جديد"
            size={isMobile ? "full" : isTablet ? "fit" : "large"}
            variant="fill"
            onClick={handleAddClick}
            icon={<img src={AddGif} alt="" />}
          />
        ) : (
          <Tooltip title="ليس لديك صلاحية لإضافة شرط جديد">
            <div>
              <BlueButton
                text="اضافة شرط جديد"
                size={isMobile ? "full" : isTablet ? "fit" : "large"}
                variant="outlined"
                onClick={handleAddClick}
                icon={<img src={AddGif} alt="" />}
                disabled={true}
                style={{ opacity: 0.5 }}
              />
            </div>
          </Tooltip>
        )}
      </div>

      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={columns}
        rows={data.map((term, index) => ({
          ...term,
          // Ensure each row has an id property
          id: term._id,
          // Add index for numbering
          index: (page - 1) * pageSize + index + 1,
        }))}
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        loading={loading}
        pagination
        paginationMode="server"
        rowCount={totalCount}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page: page - 1 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        onPaginationModelChange={(model) => {
          // Handle page change
          if (model.page !== page - 1) {
            setPage(model.page + 1);
          }

          // Handle page size change
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            setPage(1); // Reset to first page when changing page size
          }
        }}
        getRowClassName={(params) => "data-grid-row"}
        localeText={{
          MuiTablePagination: {
            labelRowsPerPage: "شرط في الصفحة",
          },
        }}
      />

      {/* Only render modals if user has appropriate permissions */}
      {(canAdd || canEdit) && (
        <SwipeableModals
          open={isOpen}
          onClose={handleClose}
          onOpen={() => setIsOpen(true)}
          currentView={currentModalView}
          initialData={selectedTerm}
          refreshData={() => setReFetch(!reFetch)}
        />
      )}

      {/* Only render delete modal if user has delete permission */}
      {canDelete && (
        <MessageModals
          open={openDelete}
          currentView="delete"
          deleteMessage={"تم حذف الشرط بنجاح"}
          route={`/system/terms-conditions/${termToDelete?._id}`}
          onDataChange={() => setReFetch(!reFetch)}
          onClose={() => {
            setOpenDelete(false);
            setTermToDelete(null);
          }}
        />
      )}

      {/* Permission status indicator */}
      {canView && (!canAdd || !canEdit || !canDelete) && (
        <div
          style={{
            marginTop: "20px",
            padding: "15px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "8px",
            fontSize: "14px",
            color: "#856404",
          }}
        >
          <strong>ملاحظة:</strong>
          {!canAdd && " • ليس لديك صلاحية لإضافة شروط جديدة"}
          {!canEdit && " • ليس لديك صلاحية لتعديل الشروط"}
          {!canDelete && " • ليس لديك صلاحية لحذف الشروط"}
        </div>
      )}
    </div>
  );
};

export default Terms;
